import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Space, Dropdown, ConfigProvider, theme as antTheme, Drawer, Button } from 'antd';
import {
  DashboardOutlined, UserOutlined, TeamOutlined, BookOutlined,
  SettingOutlined, BellOutlined, BarChartOutlined, FileTextOutlined,
  SafetyOutlined, FlagOutlined, MobileOutlined, MonitorOutlined,
  LogoutOutlined, ReadOutlined, CaretDownOutlined, MenuOutlined, CloseOutlined,
  ImportOutlined, QuestionCircleOutlined, StopOutlined, ClockCircleOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '@/stores/authStore';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const { Sider, Header, Content } = Layout;

const SIDEBAR_BG = '#1e293b';
const SIDEBAR_BORDER = 'rgba(255,255,255,0.06)';

const mainMenuItems = [
  { key: '/admin', icon: <DashboardOutlined />, label: 'Dashboard' },
  { key: '/admin/users', icon: <UserOutlined />, label: 'Users' },
  { key: '/admin/teachers', icon: <TeamOutlined />, label: 'Teachers' },
  { key: '/admin/students', icon: <ReadOutlined />, label: 'Students' },
  { key: '/admin/parents', icon: <TeamOutlined />, label: 'Parents' },
  { key: '/admin/curriculum', icon: <BookOutlined />, label: 'Curriculum' },
  { key: '/admin/config', icon: <SettingOutlined />, label: 'Config' },
  { key: '/admin/notifications', icon: <BellOutlined />, label: 'Notifications' },
  { key: '/admin/analytics', icon: <BarChartOutlined />, label: 'Analytics' },
  { key: '/admin/audit-logs', icon: <FileTextOutlined />, label: 'Audit Logs' },
  { key: '/admin/import', icon: <ImportOutlined />, label: 'Bulk Import' },
  { key: '/admin/library', icon: <BookOutlined />, label: 'Question Library' },
  { key: '/admin/help-requests', icon: <QuestionCircleOutlined />, label: 'Help Requests' },
  { key: '/admin/moderation/blocked-domains', icon: <StopOutlined />, label: 'Blocked Domains' },
  { key: '/admin/moderation/students', icon: <SafetyOutlined />, label: 'Student Moderation' },
  { key: '/admin/teachers/pending', icon: <ClockCircleOutlined />, label: 'Pending Teachers' },
  { key: '/admin/students/bulk-import', icon: <UsergroupAddOutlined />, label: 'Student Bulk Import' },
];

const superMenuItems = [
  { key: '/admin/super/admins', icon: <SafetyOutlined />, label: 'Manage Admins' },
  { key: '/admin/super/feature-flags', icon: <FlagOutlined />, label: 'Feature Flags' },
  { key: '/admin/super/app-versions', icon: <MobileOutlined />, label: 'App Versions' },
  { key: '/admin/super/system', icon: <MonitorOutlined />, label: 'System Health' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, clearAuth } = useAuthStore();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const queryClient = useQueryClient();

  const userMenu = useMemo(() => ({
    items: [{ key: 'logout', icon: <LogoutOutlined />, label: 'Sign out', danger: true }],
    onClick: ({ key }: { key: string }) => {
      if (key === 'logout') { clearAuth(); queryClient.clear(); navigate('/login'); }
    },
  }), [clearAuth, queryClient, navigate]);

  const handleNavigate = (key: string) => {
    navigate(key);
    if (isMobile) setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div style={{ padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${SIDEBAR_BORDER}` }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 8px rgba(99,102,241,0.4)' }}>
          <span style={{ color: 'white', fontWeight: 800, fontSize: 15, letterSpacing: '-0.5px' }}>C</span>
        </div>
        <div>
          <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 14, lineHeight: 1.2 }}>CardWise</div>
          <div style={{ color: '#64748b', fontSize: 10, lineHeight: 1.4 }}>Admin Portal</div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="cw-section-label" style={{ marginTop: 8 }}>Main</div>
      <Menu
        mode="inline"
        theme="dark"
        className="cw-dark-menu"
        selectedKeys={[location.pathname]}
        items={mainMenuItems}
        onClick={({ key }) => handleNavigate(key)}
        style={{ background: 'transparent', border: 'none' }}
      />

      {/* Super Admin section */}
      {isSuperAdmin && (
        <>
          <div style={{ margin: '8px 16px 0', borderTop: `1px solid ${SIDEBAR_BORDER}` }} />
          <div className="cw-section-label">Super Admin</div>
          <Menu
            mode="inline"
            theme="dark"
            className="cw-dark-menu"
            selectedKeys={[location.pathname]}
            items={superMenuItems}
            onClick={({ key }) => handleNavigate(key)}
            style={{ background: 'transparent', border: 'none' }}
          />
        </>
      )}
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Desktop Sidebar ── */}
      {!isMobile && (
        <ConfigProvider
          theme={{
            algorithm: antTheme.darkAlgorithm,
            token: { colorPrimary: '#6366f1', colorBgBase: SIDEBAR_BG },
            components: {
              Layout: { siderBg: SIDEBAR_BG },
            },
          }}
        >
          <Sider
            width={220}
            theme="dark"
            style={{
              background: SIDEBAR_BG,
              borderRight: `1px solid ${SIDEBAR_BORDER}`,
              overflow: 'auto',
              height: '100vh',
              position: 'sticky',
              top: 0
            }}
          >
            <SidebarContent />
          </Sider>
        </ConfigProvider>
      )}

      {/* ── Mobile Sidebar Drawer ── */}
      {isMobile && (
        <ConfigProvider
          theme={{
            algorithm: antTheme.darkAlgorithm,
            token: { colorPrimary: '#6366f1', colorBgBase: SIDEBAR_BG },
            components: {
              Layout: { siderBg: SIDEBAR_BG },
            },
          }}
        >
          <Drawer
            title={null}
            placement="left"
            onClose={() => setSidebarOpen(false)}
            open={sidebarOpen}
            closeIcon={<CloseOutlined />}
            bodyStyle={{ padding: 0, background: SIDEBAR_BG }}
            width={220}
          >
            <SidebarContent />
          </Drawer>
        </ConfigProvider>
      )}

      {/* ── Main area ── */}
      <Layout style={{ background: '#f8fafc' }}>
        <Header style={{
          background: '#ffffff',
          padding: isMobile ? '0 12px' : '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #e2e8f0',
          height: isMobile ? 48 : 56,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          flexShrink: 0,
          gap: 12,
        }}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setSidebarOpen(true)}
              className="sidebar-toggle-btn"
              style={{
                minWidth: 44,
                minHeight: 44,
                width: 44,
                height: 44,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 1001,
                backgroundColor: 'transparent',
                border: 'none',
                color: '#374151',
                fontSize: 18,
              }}
            />
          )}

          <span style={{
            fontWeight: 700,
            fontSize: 'clamp(13px, 4vw, 15px)',
            color: '#0f172a',
            letterSpacing: '-0.2px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
          }}>
            CardWise Admin
          </span>

          <Dropdown menu={userMenu} trigger={['click']}>
            <Space
              style={{
                cursor: 'pointer',
                padding: isMobile ? '4px 4px' : '4px 8px',
                borderRadius: 8,
                transition: 'background 0.15s',
                flexShrink: 0,
                minHeight: 44,
                alignItems: 'center',
              }}
              className="cw-user-trigger"
            >
              <Avatar
                size={isMobile ? 32 : 28}
                style={{
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  fontWeight: 700,
                  fontSize: 12
                }}
              >
                {user?.name?.[0]?.toUpperCase()}
              </Avatar>
              {!isMobile && (
                <>
                  <span
                    style={{
                      fontWeight: 500,
                      color: '#0f172a',
                      fontSize: 'clamp(12px, 3vw, 14px)',
                      display: 'none'
                    }}
                    className="user-name-desktop"
                  >
                    {user?.name}
                  </span>
                  <span
                    style={{
                      fontSize: 'clamp(10px, 2.5vw, 11px)',
                      color: '#94a3b8',
                      background: '#f1f5f9',
                      padding: '2px 6px',
                      borderRadius: 20,
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {user?.role}
                  </span>
                </>
              )}
              <CaretDownOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
            </Space>
          </Dropdown>
        </Header>

        <Content style={{
          margin: isMobile ? '8px' : '16px',
          minHeight: 280,
          padding: 'clamp(8px, 3vw, 24px)',
          overflow: 'auto'
        }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
